import dayjs, { Dayjs } from 'dayjs';
import {
  BlockedReason,
  CalculatedOrderStatus,
  DeliveryContentType,
  DeliveryType,
  DriverLocation,
  DriverState,
  Order,
  OrderState,
  Problem,
  ServiceAttempt,
} from '../models';
import { ShipperTerritory } from '../models/shipperTerritory.models';
import { extractRange, isToday, NormalizedRange } from './dateTimes';

import dayjsMinMax from 'dayjs/plugin/minMax';
import { isNil } from 'lodash';
import { CallbackTranslation } from '../locale/localizationContext';

dayjs.extend(dayjsMinMax);

export const isExpectedServiceDateAndTimeSlotExceeded = (
  order: Order,
  shipperTerritory: ShipperTerritory
) => {
  const tolerance = shipperTerritory?.uiParams?.endRangeTolerance;
  const expectedServiceDateAndTimeSlot = extractRange(
    order.expectedServiceDateAndTimeSlot!
  );
  let timeLimit: Dayjs;
  if (typeof tolerance !== 'number') {
    timeLimit = expectedServiceDateAndTimeSlot.from;
  } else {
    timeLimit = dayjs.max([
      dayjs(expectedServiceDateAndTimeSlot.from),
      dayjs(expectedServiceDateAndTimeSlot.to).subtract(tolerance, 'minutes'),
    ]);
  }

  return (
    dayjs().toDate() > timeLimit.toDate() &&
    ![OrderState.Loading, OrderState.Loaded].includes(order.status)
  );
};

export const isMaskedEnabled = (
  order: Order,
  shipperTerritory: ShipperTerritory
) => {
  let expectedServiceDateAndTimeSlot: NormalizedRange | undefined;

  if (order.expectedServiceDateAndTimeSlot) {
    expectedServiceDateAndTimeSlot = extractRange(
      order.expectedServiceDateAndTimeSlot
    );
  }

  return (
    shipperTerritory.uiParams.enableMaskedTemplate &&
    order.deliveryType === DeliveryType.Delivery &&
    expectedServiceDateAndTimeSlot &&
    !isToday(expectedServiceDateAndTimeSlot?.from)
  );
};

export const canOrderBePlanned = (
  order: Order,
  shipperTerritory: ShipperTerritory
) => {
  switch (shipperTerritory.appointmentAvailableAt) {
    case 'none':
      return false;
    case 'all':
      return true;
    default:
      try {
        return (
          order.packageProgress >= +shipperTerritory.appointmentAvailableAt
        );
      } catch {
        return false;
      }
  }
};

export const getCorrectStatus = (order: Order) => {
  const expectedServiceDateAndTimeSlotExceeded =
    isExpectedServiceDateAndTimeSlotExceeded(
      order,
      order.shipperTerritoryConfiguration
    );
  const orderCanBePlanned = canOrderBePlanned(
    order,
    order.shipperTerritoryConfiguration
  );
  const maskedEnabled = isMaskedEnabled(
    order,
    order.shipperTerritoryConfiguration
  );

  if (order.suspended) {
    return CalculatedOrderStatus.Suspended;
  }

  if (order.deliveryContentType === DeliveryContentType.Return) {
    //RETURN: 'Return'
    return CalculatedOrderStatus.Failed;
  }

  switch (order.status) {
    case OrderState.Blocked:
      const isGeocodingKo =
        order.blockedReasons &&
        order.blockedReasons.length > 0 &&
        order.serviceAttempts?.length === 0 &&
        (order.blockedReasons[0] === BlockedReason.B03 ||
          order.blockedReasons[0] === BlockedReason.B04);

      if (isGeocodingKo) {
        return CalculatedOrderStatus.GeocodingKo;
      } else {
        return CalculatedOrderStatus.Blocked;
      }

    case OrderState.Loading:
    case OrderState.Loaded:
    case OrderState.Unassigned:
    case OrderState.Baseline:
    case OrderState.Committed:
      if (order.reception && Object.keys(order.reception).length > 0) {
        return CalculatedOrderStatus.ScheduledReception;
      }
      if (expectedServiceDateAndTimeSlotExceeded) {
        if (orderCanBePlanned) {
          return CalculatedOrderStatus.StandBy;
        } else {
          return CalculatedOrderStatus.Missed;
        }
      } else if (orderCanBePlanned) {
        if (maskedEnabled) {
          return CalculatedOrderStatus.Masked;
        } else {
          return CalculatedOrderStatus.Scheduled;
        }
      } else if (order.shipperTerritoryConfiguration.uiParams.protectionMode) {
        return CalculatedOrderStatus.ProtectionMode;
      } else {
        return CalculatedOrderStatus.TooSoon;
      }

    case OrderState.InTransit:
      if (order.showMap && order.driverLocation) {
        return CalculatedOrderStatus.Arriving;
      } else {
        return CalculatedOrderStatus.InTransit;
      }

    case OrderState.Fulfilled:
      return CalculatedOrderStatus.Fulfilled;

    case OrderState.Failed:
      return CalculatedOrderStatus.Failed;

    case OrderState.ToReschedule:
      return CalculatedOrderStatus.ToReschedule;

    case OrderState.Deleted:
      return CalculatedOrderStatus.Deleted;

    default:
      return undefined;
  }
};

export const extractLatestServiceAttemptIfExist = (
  order: Order | undefined
) => {
  const serviceAttempts = order?.serviceAttempts;

  const isServiceAttemptExist =
    !isNil(serviceAttempts) && serviceAttempts.length > 0;

  if (isServiceAttemptExist) {
    let latestAttempt: ServiceAttempt = serviceAttempts[0];
    serviceAttempts.forEach((s) => {
      const difference = dayjs(latestAttempt.timestamp).diff(s.timestamp);
      if (difference < 0) {
        latestAttempt = s;
      }
    });
    return latestAttempt;
  } else {
    return null;
  }
};

export const getFunnel = (order: Order | undefined) => {
  const extractedFunnel = order?.funnel;
  return extractedFunnel && extractRange(extractedFunnel);
};

export const getExpectedServiceDateAndTimeSlot = (order: Order | undefined) => {
  if (isNil(order?.expectedServiceDateAndTimeSlot)) return null;
  return extractRange(order?.expectedServiceDateAndTimeSlot!);
};

export const getDriverInfo = (
  order: Order | undefined,
  calculatedOrderStatus: CalculatedOrderStatus | undefined,
  driverLocation: DriverLocation | undefined
) => {
  const isDriverValid = isNil(order?.driver) || isNil(order?.driverLocation);

  if (isDriverValid) return undefined;

  const etaInDate = order?.driverLocation?.eta
    ? dayjs(driverLocation?.eta || order!.driverLocation!.eta)
    : undefined;
  const isArriving = calculatedOrderStatus === CalculatedOrderStatus.Arriving;
  let arrivingState: DriverState = DriverState.Arraving;

  if (driverLocation?.arrived || order?.driverLocation?.arrived) {
    arrivingState = DriverState.Arrived;
  } else if (driverLocation?.bubbleIn || order?.driverLocation?.bubbleIn) {
    arrivingState = DriverState.BubbleIn;
  }

  return {
    driverName: order?.driver?.firstName!,
    isArriving: Boolean(isArriving),
    eta: etaInDate!,
    state: arrivingState,
    happyImage: order?.driver?.imageHappy,
    sadImage: order?.driver?.imageSad,
    lat:
      driverLocation?.gpsDriverData?.lat ||
      order?.driverLocation?.gpsDriverData?.lat,
    lng:
      driverLocation?.gpsDriverData?.lng ||
      order?.driverLocation?.gpsDriverData?.lng,
  };
};

export function isDriverLocationNecessary(
  order: Order | undefined,
  isDriverArriving: boolean | undefined,
  calculatedOrderStatus: CalculatedOrderStatus | undefined
): boolean | undefined {
  return (
    isNil(order?.opaqueId) ||
    !isDriverArriving ||
    calculatedOrderStatus !== CalculatedOrderStatus.Arriving
  );
}

export const getProblemFromBlockedReasons = (blockedReason: BlockedReason) => {
  switch (blockedReason) {
    case BlockedReason.B03:
    case BlockedReason.B02:
      return Problem.F09;
    case BlockedReason.B04:
      return Problem.F03;
    case BlockedReason.B05:
      return Problem.F05;
    case BlockedReason.B06:
      return Problem.F11;
    default:
      return undefined;
  }
};

export const getProblem = (order: Order | undefined) => {
  const isServiceAttemptExist =
    !isNil(order) &&
    !isNil(order.serviceAttempts) &&
    order.serviceAttempts.length > 0;

  const lastServiceAttempt = isServiceAttemptExist
    ? getLastServiceAttempt(order.serviceAttempts!)
    : null;

  const isFailureReasonsExists = lastServiceAttempt?.failureReason;

  const isBlockedReasonsExists =
    !isNil(order) &&
    !isNil(order.blockedReasons) &&
    order.blockedReasons.length > 0;

  if (isFailureReasonsExists) return lastServiceAttempt.failureReason;
  if (isBlockedReasonsExists)
    return getProblemFromBlockedReasons(order!.blockedReasons![0]);
};

export const getLastServiceAttempt = (serviceAttempts: ServiceAttempt[]) => {
  let lastServiceAttempt = serviceAttempts[0];
  serviceAttempts.forEach((sa) => {
    const isServiceAttemptAfter = dayjs(sa.timestamp).isAfter(
      lastServiceAttempt.timestamp
    );
    if (isServiceAttemptAfter) lastServiceAttempt = sa;
  });
  return lastServiceAttempt;
};

export const getOrderDescription = (
  orderState: OrderState,
  suspended: boolean,
  t: CallbackTranslation
): string | undefined => {
  if (suspended) return t('orders.suspended');

  switch (orderState) {
    case OrderState.Blocked:
      return t('orders.blocked');
    case OrderState.ToReschedule:
      return t('orders.toReschedule');
    case OrderState.Fulfilled:
      return t('orders.fulfilled');
    case OrderState.InTransit:
      return t('orders.inTransit');
    default:
      return undefined;
  }
};
